import classNames from 'classnames'

import {Count, Plural, PluralBlock} from '../../common/components/Plural.js'
import {FormattedRealDate} from '../../common/components/FormattedDate.js'
import RowAddress from '../../common/components/List/RowAddress.js'
import Checkbox from '../../common/components/Checkbox.js'
import ButtonLink from '../../common/components/Button/ButtonLink.js'
import Quantity from '../../common/components/Quantity.js'
import {
  warehousesSelector,
  getWarehouseName,
  getWarehouseAddress,
} from '../../data/warehouses.js'
import {transferSelector} from '../../data/transfers.js'
import {
  transferListSelectTransferID,
  transferListToggleExpanded,
  createIsSelectedSelector,
  createIsExpandedSelector,
} from './transferListFunctions.js'
import {useSelector} from '../../store.js'
import {useMemo} from 'react'
import {showTransferNotesModal} from './Modals/EditTransferParamModal.js'
import Icon from '../../common/icons/Icon.js'
import {transferDetailURLSelector} from '../TransferDetailPage/transferDetailFunctions.js'
import Link from '../../common/components/Link/Link.js'
import {getProductDetailURL} from '../ProductDetailPage/productDetailSelectors.js'
import {formatDateTime} from '../../common/date.js'

/**
 * Transfer Row component for displaying a single inventory transfer in the list.
 *
 * @param {Object} props - The component props.
 * @param {String} props.transferID - The ID of the Transfer.
 *
 * @returns {React.ReactNode} The rendered component.
 */
export default function Row({transferID}) {
  const [isSelectedSelector, isExpandedSelector] = useMemo(() => {
    return [
      createIsSelectedSelector(transferID),
      createIsExpandedSelector(transferID),
    ]
  }, [transferID])

  const isSelected = useSelector(isSelectedSelector)
  const isExpanded = useSelector(isExpandedSelector)
  const warehousesByID = useSelector(warehousesSelector)
  const transfer = useSelector((state) => transferSelector(state, {transferID}))
  const detailURL = useSelector((state) =>
    transferDetailURLSelector(state, {transferID}),
  )

  const sendingWarehouse = warehousesByID[transfer.sending_warehouse.id]
  const receivingWarehouse = warehousesByID[transfer.receiving_warehouse.id]

  return (
    <tr
      className={classNames('table__tr table__tr--transfer-row', {
        'table__tr--expanded': isExpanded,
      })}
    >
      <td className="table__td table__td--order-checkbox relative">
        <Checkbox
          checked={isSelected}
          onClick={(checked, event) =>
            transferListSelectTransferID(transferID, checked, event.shiftKey)
          }
        />
      </td>
      <td className="table__td">
        <div className="wrap--product-name inline-block">
          <a
            className="btn btn--link darkest fs-n0 lh-md margin-right-5"
            href={detailURL}
          >
            <strong>{transferID}</strong>
          </a>
        </div>
        <dl className="wrap--po-addl-info list list--order-data with-top-divider">
          <dt className="list__title--order-data">Notes</dt>
          {transfer.transfer_notes ? (
            <dd className="list__item--order-data">
              <div className="ws-pre-wrap">{transfer.transfer_notes}</div>
              <ButtonLink
                className="meta-transferlist-button-edit-internal-note"
                onClick={() => showTransferNotesModal(transferID)}
              >
                Edit
              </ButtonLink>
            </dd>
          ) : (
            <dd className="list__item--order-data">
              <ButtonLink
                className="meta-transferlist-button-add-internal-note"
                onClick={() => showTransferNotesModal(transferID)}
              >
                Add note
              </ButtonLink>
            </dd>
          )}
        </dl>
      </td>
      <td className="table__td">
        <div className="fs-n0 lh-sm-md">
          <strong>
            <PluralBlock array={transfer.lines}>
              <Count /> <Plural word="SKU" />
            </PluralBlock>
          </strong>
        </div>
        {isExpanded && transfer.lines.length > 0 && (
          <div>
            <ul className="list list--square">
              {transfer.lines.map(({sku, name, quantity}) => (
                <li className="fs-n0 lh-sm margin-top-7" key={sku}>
                  <Link href={getProductDetailURL(sku)}>
                    {name}: <Quantity value={quantity} />
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        )}
      </td>
      <td className="table__td">
        <div>
          <strong className="fs-n0 lh-md">
            {getWarehouseName(sendingWarehouse)}
          </strong>
        </div>
        <div className="wrap--po-addl-info divider--top">
          <RowAddress
            address={getWarehouseAddress(sendingWarehouse)}
            noPhone
            noEmail
          />
        </div>
      </td>
      <td className="table__td">
        <div>
          <strong className="fs-n0 lh-md">
            {getWarehouseName(receivingWarehouse)}
          </strong>
        </div>
        <div className="wrap--po-addl-info divider--top">
          <RowAddress
            address={getWarehouseAddress(receivingWarehouse)}
            noPhone
            noEmail
          />
        </div>
      </td>
      <td className="table__td">
        <div className="fs-n0 lh-md margin-bottom-1">
          <FormattedRealDate value={transfer.created} format={formatDateTime} />
        </div>
        <em className="fs-n2 text--lt-grey">
          Updated:{' '}
          <FormattedRealDate value={transfer.updated} format={formatDateTime} />
        </em>
      </td>
      <td className="table__td align-right">
        <ButtonLink
          className="no-underline btn--expand-product-row"
          onClick={() => transferListToggleExpanded(transferID)}
        >
          <Icon name="expand" className="fs-01 lh-0 v-align-middle" />
        </ButtonLink>
      </td>
    </tr>
  )
}
